import { lazy } from 'react';
import { trackRouteImport } from './chunkLoadState';

// How long to wait before the single retry.
//
// Scope, honestly: per the HTML spec the document's module map records a failed
// module fetch, so re-importing the SAME specifier rejects without another
// network request. Chromium implements that; Firefox and Safari refetch. So this
// retry helps on those engines and is a no-op on Chromium, and it is NOT what
// rescues a stale deploy — the `vite:preloadError` reload in src/index.jsx is.
// It is kept because it costs one 300 ms timer only on a path that has already
// failed, and it rejects with the original error.
const RETRY_DELAY_MS = 300;

// A route lazy is an import a user is waiting on, so it counts as one for the
// stale-deploy reload: see modules/Common/utils/chunkLoadState.js, which owns
// that state and explains who reads it.

/**
 * `React.lazy` with one retry on a failed chunk fetch.
 *
 * Why this exists rather than bare `React.lazy`:
 *
 * React's lazy payload is a little state machine — Uninitialized -> Pending ->
 * Resolved | Rejected — and **Rejected is permanent**. Once the import rejects,
 * React stores that error and re-throws the stored copy on every subsequent
 * render; it never calls the importer again. So the obvious recovery button:
 *
 *     <button onClick={resetErrorBoundary}>Try again</button>
 *
 * resets the *boundary*, re-renders the children, hits the same latched payload
 * and throws instantly. The user sees a flicker and the same error, forever.
 * That is the ErrorPage re-entry loop documented at
 * `modules/App/pages/ErrorPage.jsx:6-25`, one level down.
 *
 * Retrying therefore has to happen INSIDE the importer, before the payload
 * settles — which is what this does. Anything that needs to recover after the
 * payload has already rejected must build a new lazy component or reload the
 * page; there is no third option.
 *
 * See docs/features/react-lazy-loading/phase-0-foundation.md (D5).
 *
 * @param {() => Promise<{ default: React.ComponentType }>} importer
 * @returns {React.LazyExoticComponent}
 */
export function lazyWithRetry(importer) {
  return lazy(() =>
    trackRouteImport(() =>
      importer().catch(
        (error) =>
          new Promise((resolve, reject) => {
            setTimeout(() => {
              // One retry only. A second failure is a real failure — a stale
              // deploy or an offline client — and belongs to the error boundary
              // and the `vite:preloadError` reload in src/index.jsx, not to an
              // unbounded loop here.
              importer().then(resolve, () => reject(error));
            }, RETRY_DELAY_MS);
          }),
      ),
    ),
  );
}

export default lazyWithRetry;
